import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ScoreHeader } from "../components/ScoreHeader";
import { TrumpBadge } from "../components/TrumpBadge";
import { useAuth } from "../context/AuthContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { api, notify } from "../lib/api";
import type { GameDetail } from "../types/domain";

function contractText(hand: {
  contractFamily: string;
  contractValue: number | null;
}): string {
  switch (hand.contractFamily) {
    case "couleur":
    case "sans_atout":
    case "tout_atout":
      return String(hand.contractValue ?? 0);
    case "capot":
      return "Capot";
    case "generale":
      return "Générale";
    default:
      return "";
  }
}

export function GameDetailPage() {
  const { gameId = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setTrail } = useBreadcrumbs();
  const [game, setGame] = useState<GameDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    "submit" | "delete" | null
  >(null);

  useEffect(() => {
    let active = true;
    setTrail([]);
    void api.getGame(gameId).then((g) => {
      if (!active) return;
      setGame(g);
      setTrail(
        g.tournamentId && g.tournamentName
          ? [
              { label: "Parties", to: "/games" },
              {
                label: g.tournamentName,
                to: `/tournaments/${g.tournamentId}`,
              },
              { label: g.title },
            ]
          : [{ label: "Parties", to: "/games" }, { label: g.title }],
      );
    });
    return () => {
      active = false;
    };
  }, [gameId, setTrail]);

  if (!game) {
    return (
      <div className="mx-auto max-w-lg space-y-5 px-4 py-8">
        <div className="score-panel animate-pulse">
          <div className="h-3 w-24 rounded bg-white/20" />
          <div className="mt-3 grid grid-cols-2 gap-4">
            <div className="h-10 rounded bg-white/20" />
            <div className="h-10 rounded bg-white/20" />
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 w-1/2 rounded bg-gray-200" />
              <div className="mt-2 h-3 w-1/3 rounded bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isTournament = game.mode === "tournament";
  const isInProgress = game.status === "in_progress";
  const isSubmitted = game.status === "submitted";
  const isAdmin = user?.isAdmin ?? false;
  const canWrite = game.isParticipant || isAdmin;
  const canAddHand = (() => {
    if (!canWrite) return false;
    if (game.status === null) return true;
    if (!isInProgress) return false;
    if (!isTournament || game.quotaHands == null) return true;
    const quota = game.quotaHands;
    const handCount = game.hands.length;
    const leftRemaining = Math.max(
      0,
      quota - (game.countedHandsBeforeLeft ?? 0),
    );
    const rightRemaining = Math.max(
      0,
      quota - (game.countedHandsBeforeRight ?? 0),
    );
    return handCount < leftRemaining || handCount < rightRemaining;
  })();
  const canSubmit = isTournament && isInProgress && game.hands.length > 0;
  const canValidateOrDispute =
    isTournament &&
    isSubmitted &&
    game.mySide !== null &&
    game.submittedBySide !== null &&
    game.mySide !== game.submittedBySide;
  const canEdit = !isTournament && game.handsEditable && canWrite;
  const canDelete = canWrite && !(isTournament && game.status === "validated");

  async function handleDelete() {
    setBusy(true);
    setPendingAction(null);
    try {
      await api.deleteGame(gameId);
      notify("success", "Partie supprimée");
      navigate("/games");
    } catch {
      // handled
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit() {
    setBusy(true);
    setPendingAction(null);
    try {
      const updated = await api.submitGame(gameId);
      setGame(updated);
    } catch {
      // handled
    } finally {
      setBusy(false);
    }
  }

  async function handleValidate() {
    setBusy(true);
    try {
      const updated = await api.validateGame(gameId);
      setGame(updated);
    } catch {
      // handled
    } finally {
      setBusy(false);
    }
  }

  async function handleDispute() {
    setBusy(true);
    try {
      const updated = await api.disputeGame(gameId);
      setGame(updated);
    } catch {
      // handled
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteHand(handId: string) {
    setBusy(true);
    try {
      const updated = await api.deleteHand(gameId, handId);
      setGame(updated);
      notify("success", "Levée supprimée");
    } catch {
      // handled by notification
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-5 px-4 py-6">
      <ScoreHeader
        variant={isTournament ? "tournament" : "quick"}
        modeLabel={
          isTournament ? (game.tournamentName ?? "Tournoi") : "Partie rapide"
        }
        leftLabel="Nous"
        rightLabel="Eux"
        leftScore={game.scoreLeft}
        rightScore={game.scoreRight}
        targetScore={game.targetScore}
        subtitle={game.subtitle}
        status={isTournament ? game.status : undefined}
        leftPlayers={game.leftPlayers || undefined}
        rightPlayers={game.rightPlayers || undefined}
        hands={game.hands}
      />

      {(canEdit ||
        canDelete ||
        canSubmit ||
        canValidateOrDispute ||
        (isSubmitted && game.mySide === game.submittedBySide)) && (
        <div className="flex flex-wrap items-center gap-3">
          {canEdit && (
            <Link to={`/games/${game.id}/edit`} className="btn-secondary">
              Modifier
            </Link>
          )}
          {canSubmit && (
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => setPendingAction("submit")}
            >
              Soumettre la partie
            </button>
          )}
          {canValidateOrDispute && (
            <>
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={handleValidate}
              >
                Valider la partie
              </button>
              <button
                type="button"
                className="btn-secondary text-red-600"
                disabled={busy}
                onClick={handleDispute}
              >
                Contester
              </button>
            </>
          )}
          {canDelete && (
            <button
              type="button"
              className="btn-secondary text-red-500 hover:text-red-600"
              disabled={busy}
              onClick={() => setPendingAction("delete")}
            >
              Supprimer
            </button>
          )}
        </div>
      )}

      {isSubmitted && game.mySide === game.submittedBySide && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          En attente de validation par le camp adverse.
        </div>
      )}

      {game.supportScoreIgnored ? (
        <div className="rounded-2xl border border-dashed border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Partie à blanc : le score réel est conservé, mais le score support ne
          compte pas pour le classement tournoi.
        </div>
      ) : null}

      <hr className="border-gray-200" />

      {game.hands.length === 0 ? (
        <p className="py-6 text-center text-sm text-smoke">
          Aucune levée pour l&apos;instant.
        </p>
      ) : (
        <div className="space-y-3">
          {(() => {
            const target = game.targetScore;
            const quota = game.quotaHands;
            const leftQuotaAt =
              quota != null && game.countedHandsBeforeLeft != null
                ? Math.max(0, quota - game.countedHandsBeforeLeft)
                : null;
            const rightQuotaAt =
              quota != null && game.countedHandsBeforeRight != null
                ? Math.max(0, quota - game.countedHandsBeforeRight)
                : null;

            let runLeft = 0;
            let runRight = 0;
            let targetDividerPlaced = false;
            let leftQuotaDividerPlaced = false;
            let rightQuotaDividerPlaced = false;

            const targetDivider = (key: string) =>
              target ? (
                <div
                  key={key}
                  className="flex items-center gap-3 py-1 text-xs font-medium text-smoke"
                >
                  <hr className="flex-1 border-dashed border-gray-300" />
                  <span className="tabular-nums">{target} pts</span>
                  <hr className="flex-1 border-dashed border-gray-300" />
                </div>
              ) : null;

            const quotaDivider = (key: string, teamName: string) => (
              <div
                key={key}
                className="flex items-center gap-3 py-1 text-xs font-medium text-amber-600"
              >
                <hr className="flex-1 border-dashed border-amber-300" />
                <span>Quota atteint pour {teamName}</span>
                <hr className="flex-1 border-dashed border-amber-300" />
              </div>
            );

            const elements: React.ReactNode[] = [];

            game.hands.forEach((hand, index) => {
              runLeft += hand.leftScore;
              runRight += hand.rightScore;
              const handNumber = index + 1;

              elements.push(
                <div
                  key={hand.id}
                  className="card transition hover:border-gray-400"
                  style={
                    hand.takerSide === "left"
                      ? {
                          borderLeftColor: hand.success ? "#10b981" : "#f43f5e",
                          borderLeftWidth: "3px",
                        }
                      : {
                          borderRightColor: hand.success
                            ? "#10b981"
                            : "#f43f5e",
                          borderRightWidth: "3px",
                        }
                  }
                >
                  {(() => {
                    const content = (
                      <>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-2xl font-bold tabular-nums text-ink w-16">
                            {hand.leftScore}
                          </div>
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] tabular-nums text-smoke/60">
                                ({hand.pointsLeft})
                              </span>
                              <span className="text-base font-semibold text-ink/70">
                                {contractText(hand)}{" "}
                                <TrumpBadge trump={hand.trump} />
                              </span>
                              <span className="text-[10px] tabular-nums text-smoke/60">
                                ({hand.pointsRight})
                              </span>
                            </div>
                            <span className="text-xs font-medium text-smoke/60 tabular-nums">
                              #{hand.orderIndex}
                            </span>
                          </div>
                          <div className="text-2xl font-bold tabular-nums text-right text-ink w-16">
                            {hand.rightScore}
                          </div>
                        </div>
                        {(() => {
                          const coincheSide =
                            hand.takerSide === "left" ? "right" : "left";
                          const leftPills: React.ReactNode[] = [];
                          const rightPills: React.ReactNode[] = [];
                          if (hand.coincheState !== "simple") {
                            const pill = (
                              <span
                                key="coinche"
                                className="status-pill bg-amber-100 text-amber-700"
                              >
                                {hand.coincheState === "coinche"
                                  ? "Coinche"
                                  : "Surcoinche"}
                              </span>
                            );
                            if (coincheSide === "left") leftPills.push(pill);
                            else rightPills.push(pill);
                          }
                          if (hand.beloteLeft > 0)
                            leftPills.push(
                              <span
                                key="belote-left"
                                className="status-pill bg-sky-100 text-sky-700"
                              >
                                Belote
                              </span>,
                            );
                          if (hand.beloteRight > 0)
                            rightPills.push(
                              <span
                                key="belote-right"
                                className="status-pill bg-sky-100 text-sky-700"
                              >
                                Belote
                              </span>,
                            );
                          if (hand.foulSide) {
                            const pill = (
                              <span
                                key="foul"
                                className="status-pill bg-red-100 text-red-700"
                              >
                                Faute
                              </span>
                            );
                            if (hand.foulSide === "left") leftPills.push(pill);
                            else rightPills.push(pill);
                          }
                          const hasPills =
                            leftPills.length > 0 || rightPills.length > 0;
                          if (!hasPills) return null;
                          return (
                            <div className="mt-1.5 flex justify-between gap-2">
                              <div className="flex flex-wrap gap-1.5">
                                {leftPills}
                              </div>
                              <div className="flex flex-wrap justify-end gap-1.5">
                                {rightPills}
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    );
                    return canWrite && game.handsEditable ? (
                      <Link
                        to={`/games/${game.id}/hands/${hand.id}/edit`}
                        className="block no-underline"
                      >
                        {content}
                      </Link>
                    ) : (
                      <div className="block">{content}</div>
                    );
                  })()}
                  {canWrite && game.handsEditable && (
                    <div className="mt-2 flex justify-end border-t border-gray-100 pt-2">
                      <button
                        type="button"
                        className="text-xs text-gray-400 hover:text-gray-600"
                        disabled={busy}
                        onClick={() => handleDeleteHand(hand.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>,
              );

              if (
                !targetDividerPlaced &&
                target &&
                (runLeft >= target || runRight >= target)
              ) {
                elements.push(targetDivider(`target-after-${index}`));
                targetDividerPlaced = true;
              }

              if (
                !leftQuotaDividerPlaced &&
                leftQuotaAt != null &&
                leftQuotaAt > 0 &&
                handNumber === leftQuotaAt
              ) {
                elements.push(
                  quotaDivider(`quota-left-${index}`, game.quotaLabels[0]),
                );
                leftQuotaDividerPlaced = true;
              }

              if (
                !rightQuotaDividerPlaced &&
                rightQuotaAt != null &&
                rightQuotaAt > 0 &&
                handNumber === rightQuotaAt
              ) {
                elements.push(
                  quotaDivider(`quota-right-${index}`, game.quotaLabels[1]),
                );
                rightQuotaDividerPlaced = true;
              }
            });

            if (!targetDividerPlaced && target) {
              elements.push(targetDivider("target-end"));
            }

            return elements;
          })()}
        </div>
      )}

      {canAddHand && (
        <div className="flex justify-center">
          <Link to={`/games/${game.id}/hands/new`} className="btn-primary">
            +
          </Link>
        </div>
      )}

      {pendingAction === "submit" && (
        <ConfirmDialog
          title="Soumettre la partie"
          description="Le score sera transmis à l'adversaire pour validation. Cette action est irréversible."
          confirmLabel="Soumettre"
          onConfirm={handleSubmit}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {pendingAction === "delete" && (
        <ConfirmDialog
          title="Supprimer la partie"
          description="Cette partie sera définitivement supprimée. Cette action est irréversible."
          confirmLabel="Supprimer"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setPendingAction(null)}
        />
      )}
    </div>
  );
}
